import React, { useState, useEffect, useCallback } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Package, Link2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import type { Module, OrganisationWithDetails } from '../index'
import JoinLinksModal from './JoinLinksModal'

interface B2cModulesTabProps {
  organisation: OrganisationWithDetails | null
  /** The whole catalogue: a B2C organisation can take any module, no subscription needed. */
  catalogue: Module[]
  loading: boolean
}

interface B2cModule {
  id: number
  organisationId: number
  moduleId: number
  moduleName: string
  isActive: boolean
  /** Active plans that give access to this module. */
  activePlanCount: number
}

const B2C_MODULE_FIELDS = `
  id
  organisationId
  moduleId
  moduleName
  isActive
  activePlanCount
`

const GET_B2C_MODULES = gql`
  query getB2cModules($organisationId: Int!) {
    getB2cModules(organisationId: $organisationId) {
      ${B2C_MODULE_FIELDS}
    }
  }
`

const ADD_B2C_MODULE = gql`
  mutation addB2cModule($input: AddB2cModuleInput!) {
    addB2cModule(input: $input) {
      ${B2C_MODULE_FIELDS}
    }
  }
`

const UPDATE_B2C_MODULE = gql`
  mutation updateB2cModule($input: UpdateB2cModuleInput!) {
    updateB2cModule(input: $input) {
      ${B2C_MODULE_FIELDS}
    }
  }
`

const B2cModulesTab: React.FC<B2cModulesTabProps> = ({ organisation, catalogue, loading }) => {
  const [modules, setModules] = useState<B2cModule[]>([])
  const [loadingModules, setLoadingModules] = useState(false)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [addModuleId, setAddModuleId] = useState('')
  const [adding, setAdding] = useState(false)
  const [isJoinLinksOpen, setIsJoinLinksOpen] = useState(false)
  const [joinLinksModuleId, setJoinLinksModuleId] = useState<number | null>(null)
  const { showSuccess, showError } = useToast()
  const { t } = useTranslation()

  const organisationId = organisation?.id

  const fetchModules = useCallback(async () => {
    if (!organisationId) return
    try {
      setLoadingModules(true)
      const response = await authorizedGraphQLRequest<{ getB2cModules: B2cModule[] }>(GET_B2C_MODULES, { organisationId })
      setModules(response.getB2cModules || [])
    } catch (error) {
      console.error('Failed to fetch B2C modules:', error)
    } finally {
      setLoadingModules(false)
    }
  }, [organisationId])

  useEffect(() => {
    fetchModules()
  }, [fetchModules])

  const replaceModule = (updated: B2cModule) => {
    setModules((current) => current.map((m) => (m.id === updated.id ? updated : m)))
  }

  const handleAdd = async () => {
    if (!organisationId || !addModuleId) return
    try {
      setAdding(true)
      const response = await authorizedGraphQLRequest<{ addB2cModule: B2cModule }>(ADD_B2C_MODULE, {
        input: { organisationId, moduleId: Number(addModuleId) },
      })
      setModules((current) => [...current, response.addB2cModule])
      setAddModuleId('')
      showSuccess('Module added')
    } catch (err: any) {
      showError(err?.response?.errors?.[0]?.message || 'Failed to add module')
    } finally {
      setAdding(false)
    }
  }

  const handleToggleActive = async (module: B2cModule) => {
    // Deactivating hides the module from everyone who holds it, mid-use included.
    if (module.isActive && !confirm(`Deactivate "${module.moduleName}"? Subscribers keep their minutes but lose access to it, and plans including it stop being offered, until it is reactivated.`)) return
    try {
      setSavingId(module.id)
      const response = await authorizedGraphQLRequest<{ updateB2cModule: B2cModule }>(UPDATE_B2C_MODULE, { input: { id: module.id, isActive: !module.isActive } })
      replaceModule(response.updateB2cModule)
    } catch (err: any) {
      showError(err?.response?.errors?.[0]?.message || 'Failed to update module')
    } finally {
      setSavingId(null)
    }
  }

  const openJoinLinks = (moduleId: number | null) => {
    setJoinLinksModuleId(moduleId)
    setIsJoinLinksOpen(true)
  }

  if (loading || loadingModules) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  const takenModuleIds = new Set(modules.map((m) => m.moduleId))
  const addable = catalogue.filter((m) => !takenModuleIds.has(m.id))
  // A link can only carry modules a redeemer could actually be granted.
  const linkableModules = modules.filter((m) => m.isActive).map((m) => ({ id: m.moduleId, name: m.moduleName }))

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Modules ({modules.length})</h4>
        <button
          onClick={() => openJoinLinks(null)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
        >
          <Link2 className="h-4 w-4" />
          {t('joinLinks.title')}
        </button>
      </div>

      {/* Add from catalogue */}
      <div className="border border-gray-200 rounded-xl p-4">
        <h5 className="font-semibold text-gray-800 mb-1">Add a module</h5>
        <p className="text-xs text-gray-500 mb-3">No B2B subscription or seat limit applies. People reach a module through a plan that includes it (Plans tab); a join link brings them into the organisation.</p>
        {addable.length === 0 ? (
          <p className="text-sm text-gray-500">Every module in the catalogue has been added.</p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-56">
              <label className="block text-xs font-medium text-gray-600 mb-1">Module</label>
              <select
                value={addModuleId}
                onChange={(e) => setAddModuleId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select a module</option>
                {addable.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={handleAdd} disabled={adding || !addModuleId}>
              <Plus className="h-4 w-4 mr-1" />
              {adding ? 'Adding...' : 'Add module'}
            </Button>
          </div>
        )}
      </div>

      {/* Modules List */}
      {modules.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No modules yet</p>
          <p className="text-sm mt-1">Add a module, then share a join link for it</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((module) => {
            const busy = savingId === module.id

            return (
              <div key={module.id} className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm ${module.isActive ? '' : 'opacity-70'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg ${module.isActive ? 'bg-gradient-to-br from-primary to-primary/70' : 'bg-gray-300'}`}>
                      <Package className="h-5 w-5 text-white" />
                    </div>
                    <h5 className="font-semibold text-gray-800 truncate">{module.moduleName}</h5>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {module.isActive && (
                      <button
                        onClick={() => openJoinLinks(module.moduleId)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        title={t('joinLinks.forModule')}
                      >
                        <Link2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${module.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                      {module.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button onClick={() => handleToggleActive(module)} disabled={busy} className="text-sm text-primary hover:underline disabled:opacity-50">
                    {module.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                  <span className={`text-sm ${module.activePlanCount > 0 ? 'text-gray-600' : 'text-amber-700'}`}>
                    {module.activePlanCount > 0 ? `In ${module.activePlanCount} active plan${module.activePlanCount === 1 ? '' : 's'}` : 'In no active plan'}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <JoinLinksModal isOpen={isJoinLinksOpen} onClose={() => setIsJoinLinksOpen(false)} organisation={organisation} modules={linkableModules} preselectedModuleId={joinLinksModuleId} />
    </div>
  )
}

export default B2cModulesTab
